import { isValidElement, type ReactNode } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/shared/utils/cn'
import { CodeBlock } from './CodeBlock'

/**
 * POLISH 1 — safe markdown renderer for assistant answers.
 *
 * Security model (hard requirement — no XSS):
 * - Uses ``react-markdown``, which builds a React element tree and NEVER calls
 *   ``dangerouslySetInnerHTML``.
 * - ``rehype-raw`` is deliberately NOT installed/used, so any raw HTML emitted
 *   by the model is treated as inert text — it is never parsed into live DOM.
 * - ``react-markdown`` sanitizes URLs by default (its ``defaultUrlTransform``
 *   drops ``javascript:``/``vbscript:``/dangerous ``data:`` schemes); we add a
 *   second explicit guard in ``safeHref`` as defense-in-depth and force
 *   ``rel="noopener noreferrer nofollow"`` + ``target="_blank"`` on links.
 *
 * Rendering:
 * - ``remark-gfm`` adds tables, strikethrough, task lists and autolinks.
 * - Fenced/indented code blocks render via {@link CodeBlock} (grey card +
 *   copy). Inline code gets a subtle pill. Tables are wrapped so they scroll
 *   horizontally instead of widening the chat column.
 */

/** Collapse a rendered node back to its raw text (for the code copy buffer). */
function nodeToText(node: ReactNode): string {
  if (node == null || node === false || node === true) return ''
  if (typeof node === 'string') return node
  if (typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(nodeToText).join('')
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode }
    return nodeToText(props.children)
  }
  return ''
}

/**
 * Allow only safe schemes (and scheme-less relative/anchor links). ``href`` is
 * already sanitized by react-markdown; this is a belt-and-suspenders pass.
 */
function safeHref(href: unknown): string | undefined {
  if (typeof href !== 'string') return undefined
  const trimmed = href.trim()
  if (!trimmed) return undefined
  if (/^(javascript:|vbscript:|data:|file:)/i.test(trimmed)) return undefined
  return trimmed
}

const COMPONENTS: Components = {
  a(props) {
    const href = safeHref(props.href)
    if (!href) {
      // Drop the anchor entirely if the URL is unsafe; keep the visible text.
      return <span>{props.children}</span>
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={(e) => e.stopPropagation()}
      >
        {props.children}
      </a>
    )
  },
  code(props) {
    const { className, children } = props
    // Keep the language-* class so the `pre` override can read it; the inline
    // pill style is harmless on the (discarded) inner element of a code block.
    return <code className={cn('md-inline-code', className)}>{children}</code>
  },
  pre(props) {
    const child = props.children
    let language: string | undefined
    let value = ''
    if (isValidElement(child)) {
      const childProps = child.props as {
        className?: string
        children?: ReactNode
      }
      const match = /language-([\w-]+)/.exec(childProps.className ?? '')
      language = match?.[1]
      value = nodeToText(childProps.children)
    } else {
      value = nodeToText(child)
    }
    // Markdown code blocks carry a trailing newline; trim a single one.
    return <CodeBlock value={value.replace(/\n$/, '')} language={language} />
  },
  table(props) {
    // Wrap so wide tables scroll within the bubble instead of overflowing it.
    return (
      <div className="md-table-wrap">
        <table>{props.children}</table>
      </div>
    )
  },
}

interface Props {
  content: string
}

export function MarkdownMessage({ content }: Props) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
      {content}
    </ReactMarkdown>
  )
}
